import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { apiService } from "./api";

class WebSocketService {
  constructor() {
    this.stompClient = null;
    this.subscribers = new Map();
    this.connectionPromise = null;
    this.currentMobileNumber = null;
    this.messageCallback = null;
  }

  // Ensure connection is active before sending
  async ensureConnection(mobileNumber, messageCallback) {
    // If no current connection or connection is stale
    if (!this.stompClient || !this.stompClient.connected) {
      // Store mobile number and callback for potential reconnection
      this.currentMobileNumber = mobileNumber;
      this.messageCallback = messageCallback;

      // Attempt to reconnect
      await this.connect(mobileNumber, messageCallback);
    }
    return this.stompClient && this.stompClient.connected;
  }

  async connect(mobileNumber, messageCallback) {
    // If already connecting, return existing promise
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      // Clean up any existing client
      if (this.stompClient) {
        try {
          this.stompClient.deactivate();
        } catch (err) {
          console.error("Error deactivating previous client:", err);
        }
      }

      // Create new socket and STOMP client
      const socket = new SockJS(apiService.ws.baseUrl);
      this.stompClient = new Client({
        webSocketFactory: () => socket,
        reconnectDelay: 5000, // Attempt reconnect every 5 seconds
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        onConnect: () => {
          console.log("WebSocket Connected");

          // Resubscribe to messages
          this.subscribeToAgentMessages(mobileNumber, messageCallback);

          resolve(true);
        },
        onDisconnect: (frame) => {
          console.log("WebSocket Disconnected", frame);
          this.connectionPromise = null;
          this.stompClient = null;
        },
        onStompError: (frame) => {
          console.error("Broker reported error: " + frame.headers["message"]);
          console.error("Additional details: " + frame.body);
          reject(new Error("WebSocket connection error"));
        },
        onWebSocketError: (error) => {
          console.error("WebSocket Error:", error);
          reject(error);
        },
      });

      // Activate the connection
      this.stompClient.activate();
    });

    return this.connectionPromise;
  }

  subscribeToAgentMessages(mobileNumber, callback) {
    if (!this.stompClient || !this.stompClient.connected) {
      console.error("Cannot subscribe - not connected");
      return;
    }

    // Remove any existing subscription for this mobile number
    const existingSubscription = this.subscribers.get(mobileNumber);
    if (existingSubscription) {
      existingSubscription.unsubscribe();
    }

    // Create new subscription
    const subscription = this.stompClient.subscribe(
      apiService.ws.chatTopicUrl(mobileNumber),
      (message) => {
        console.log("Received message: ", message);
        try {
          const data = JSON.parse(message.body);
          callback(data);
        } catch (error) {
          console.error("Error parsing message:", error);
        }
      }
    );

    // Store the new subscription
    this.subscribers.set(mobileNumber, subscription);
  }

  async sendMessage(messageData) {
    try {
      // Ensure connection is active
      const isConnected = await this.ensureConnection(
        messageData.from,
        this.messageCallback
      );

      if (!isConnected) {
        throw new Error("Could not establish WebSocket connection");
      }

      console.log("Sending message via WebSocket:", messageData);

      // Publish message
      this.stompClient.publish({
        destination: apiService.ws.sendMessageEndpoint,
        body: JSON.stringify(messageData),
        headers: {
          "content-type": "application/json",
        },
      });

      return true;
    } catch (error) {
      console.error("WebSocket Send Message Error:", error);
      throw error;
    }
  }

  disconnect(mobileNumber) {
    // Unsubscribe from specific mobile number
    const subscription = this.subscribers.get(mobileNumber);
    if (subscription) {
      subscription.unsubscribe();
      this.subscribers.delete(mobileNumber);
    }

    // If no more subscribers, fully disconnect
    if (this.subscribers.size === 0 && this.stompClient) {
      this.stompClient.deactivate();
      this.stompClient = null;
      this.connectionPromise = null;
    }
  }
}

export const webSocketService = new WebSocketService();
